/**
 * @file threshold-detection.ts
 * @description Pure comparison logic for detecting threshold changes between
 * Intervals.icu and the local store.
 *
 * Two properties keep the "New Thresholds Detected" suggestion from
 * re-appearing constantly:
 *
 * 1. Tolerance-based comparison — threshold pace round-trips through a
 *    m/s ⇄ s/km conversion (`1000 / speed`) that can drift by ±1-2 s/km,
 *    so strict equality re-flagged values the user had already accepted.
 * 2. A stable signature of the remote values — persisted (via preferences,
 *    which sync to Supabase) once a suggestion has been shown, so the same
 *    remote values never prompt again. Only a genuine change on
 *    Intervals.icu produces a new signature and a new prompt.
 */

import type { SyncResult } from './intervals-sync';

export interface CurrentThresholds {
    /** Cycling FTP in watts. */
    ftp?: number;
    /** Running LTHR in bpm. */
    runningLthr?: number;
    /** Cycling LTHR in bpm. */
    cyclingLthr?: number;
    /** Running threshold pace in seconds per km. */
    thresholdPace?: number;
}

/** Pace drift absorbed by the m/s ⇄ s/km round-trip conversion. */
export const PACE_TOLERANCE_SECONDS_PER_KM = 2;
/** Heart-rate and power values are integers; allow ±1 for rounding. */
export const HR_TOLERANCE_BPM = 1;
export const FTP_TOLERANCE_WATTS = 1;

function differs(remote: number, current: number | undefined, tolerance: number): current is number {
    if (!current || !remote) return false;
    return Math.abs(remote - current) > tolerance;
}

/**
 * Compares remote Intervals.icu thresholds against current local values.
 * Returns human-readable change descriptions, or an empty array when all
 * values match within tolerance.
 */
export function detectThresholdChanges(
    remote: SyncResult['metrics'],
    current: CurrentThresholds
): string[] {
    const changes: string[] = [];

    if (differs(remote.ftp, current.ftp, FTP_TOLERANCE_WATTS)) {
        changes.push(`FTP: ${remote.ftp}W (was ${current.ftp}W)`);
    }
    if (differs(remote.runningLthr, current.runningLthr, HR_TOLERANCE_BPM)) {
        changes.push(`Run LTHR: ${remote.runningLthr} bpm`);
    }
    if (differs(remote.cyclingLthr, current.cyclingLthr, HR_TOLERANCE_BPM)) {
        changes.push(`Cycle LTHR: ${remote.cyclingLthr} bpm`);
    }
    if (differs(remote.thresholdPace, current.thresholdPace, PACE_TOLERANCE_SECONDS_PER_KM)) {
        changes.push('Threshold Pace');
    }

    return changes;
}

/**
 * Builds a stable identity for a set of remote threshold values, used to
 * remember which suggestion the user has already seen.
 */
export function buildThresholdSignature(remote: SyncResult['metrics']): string {
    return [
        `ftp:${remote.ftp}`,
        `runLthr:${remote.runningLthr}`,
        `cycleLthr:${remote.cyclingLthr}`,
        `pace:${remote.thresholdPace}`,
    ].join('|');
}
